import { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  SecurityOutlined as ShieldIcon,
  HistoryOutlined as HistoryIcon,
  Business as BusinessIcon,
  BadgeOutlined as BadgeIcon,
} from '@mui/icons-material';

import PageLayout from '@/components/Layout/PageLayout';
import CardHeader from '@/components/Card/CardHeader';
import UserBasicInfoSection from './UserManagement/components/UserBasicInfoSection';
import UserPermissionsSection from './UserManagement/components/UserPermissionsSection';
import UserHistorySection from './UserManagement/components/UserHistorySection';
import UserEmployeeInfoSection from './UserManagement/components/UserEmployeeInfoSection';

import { API_BASE_URL } from '@/config';
import axios from 'axios';
import { OrganizationsApi } from '@/api/clients/organizations/organizations.api';
import { UsersApi } from '@/api/clients/users/users.api';
import { useGetFacilityUser, useUpdateFacilityUser } from '@/api/hooks/users';
import { FACILITY_USER_ACCESS_MODE } from '@/constants/facilityUserAccessModes';
import { buildFacilityUserScopePayload } from './UserManagement/components/facilityUserScope.utils';

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const basicInfoRef = useRef(null);
  const employeeInfoRef = useRef(null);
  const previousRoleBasedFacilitiesAccessRef = useRef(false);

  const { user, loading, error: fetchError, refetch } = useGetFacilityUser({
    id: userId,
  });
  const {
    updateUser,
    loading: updateLoading,
    error: updateError,
  } = useUpdateFacilityUser();
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState(null);

  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl ? parseInt(tabFromUrl, 10) : 0;
  });

  const [basicInfo, setBasicInfo] = useState({});
  const [facilityPermissions, setFacilityPermissions] = useState({});
  const [organizationPermissions, setOrganizationPermissions] = useState({});
  const [permissionsType, setPermissionsType] = useState('permission');
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [accessMode, setAccessMode] = useState(FACILITY_USER_ACCESS_MODE.NONE);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [selectedFacilityGroupIds, setSelectedFacilityGroupIds] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [employeeInfo, setEmployeeInfo] = useState({});

  const [facilities, setFacilities] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [permissionsTemplate, setPermissionsTemplate] = useState([]);
  const [roleBasedFacilitiesAccess, setRoleBasedFacilitiesAccess] = useState(
    false,
  );
  const [guardError, setGuardError] = useState(null);

  useEffect(() => {
    if (user) {
      const nextAccessMode =
        user.user_data?.access_mode || FACILITY_USER_ACCESS_MODE.NONE;

      setBasicInfo({
        email: user.user_data?.email || '',
        userName: user.user_data?.name || '',
        name: user.user_data?.name || '',
        mfa_type: user.user_data?.mfa_type || 'sms',
        languagePreference: user.user_data?.language_preference || 'en',
      });

      setAccessMode(nextAccessMode);

      const facilitiesFromBackend =
        user.facilities_associated?.filter((f) => f.id !== null) || [];
      setSelectedFacilities(
        facilitiesFromBackend.map((facility) => facility.id),
      );
      setSelectedFacilityGroupIds(
        (user.assigned_facility_groups || []).map((group) => group.id),
      );

      if (user.user_data?.organization_id) {
        setSelectedOrganization(user.user_data.organization_id + '');
      }

      if (
        user.user_data?.permissions_type === 'role' &&
        user.user_data?.role_id
      ) {
        setPermissionsType('role');
        setSelectedRoleId(user.user_data.role_id);
        setFacilityPermissions({});
        setOrganizationPermissions({});
      } else {
        setPermissionsType('permission');
        setSelectedRoleId(null);

        const facilityPerms = {};
        user.facility_and_permissions?.forEach((fp) => {
          facilityPerms[fp.facilityId + ''] = fp.permissions;
        });
        setFacilityPermissions(facilityPerms);

        const orgPerms = {};
        if (user.organization_permissions?.permissions) {
          const orgId = String(user.organization_permissions.organizationId);
          orgPerms[orgId] = user.organization_permissions.permissions;
          setSelectedOrganization(orgId);
        } else if (user.user_data?.organization_id) {
          const orgId = String(user.user_data.organization_id);
          orgPerms[orgId] = {};
        }
        setOrganizationPermissions(orgPerms);
      }

      setGuardError(null);
      previousRoleBasedFacilitiesAccessRef.current = false;
      setRoleBasedFacilitiesAccess(false);
    }
  }, [user]);

  useEffect(() => {
    const previousRoleBasedFacilitiesAccess =
      previousRoleBasedFacilitiesAccessRef.current;

    if (previousRoleBasedFacilitiesAccess === roleBasedFacilitiesAccess) {
      return;
    }

    if (roleBasedFacilitiesAccess) {
      setAccessMode(FACILITY_USER_ACCESS_MODE.ALL_FACILITIES);
      setSelectedFacilities([]);
      setSelectedFacilityGroupIds([]);
      setGuardError(null);
    } else if (previousRoleBasedFacilitiesAccess) {
      setAccessMode(FACILITY_USER_ACCESS_MODE.NONE);
      setSelectedFacilities([]);
      setSelectedFacilityGroupIds([]);
    }

    previousRoleBasedFacilitiesAccessRef.current = roleBasedFacilitiesAccess;
  }, [roleBasedFacilitiesAccess]);

  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const facilitiesResponse = await axios.get(
          `${API_BASE_URL}/facilitiesPro`,
        );
        setFacilities(facilitiesResponse.data);

        const organizationsResponse = await OrganizationsApi.getOrganizations();
        setOrganizations(organizationsResponse.data.organizations);

        const permissionsResponse = await axios.get(
          `${API_BASE_URL}/permissions`,
        );
        setPermissionsTemplate(permissionsResponse.data);
      } catch (err) {
        console.error('Error fetching reference data:', err);
      }
    };

    fetchReferenceData();
  }, []);

  const handleBasicInfoChange = (newBasicInfo) => {
    setBasicInfo(newBasicInfo);
  };

  const handlePermissionChange = (
    newPermissions,
    targetId,
    permissionLevel,
  ) => {
    if (permissionLevel === 'facility') {
      setFacilityPermissions((prev) => ({
        ...prev,
        [targetId]: newPermissions,
      }));
    } else {
      setOrganizationPermissions((prev) => ({
        ...prev,
        [targetId]: newPermissions,
      }));
    }
  };

  const handlePermissionsTypeChange = (newType) => {
    if (
      accessMode === FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS &&
      newType === 'permission'
    ) {
      setGuardError(
        'Leave facility-group access before switching this user to custom permissions.',
      );
      return;
    }

    setGuardError(null);
    setPermissionsType(newType);

    if (newType === 'role') {
      setFacilityPermissions({});
      setOrganizationPermissions({});
    } else {
      setSelectedRoleId(null);
      setRoleBasedFacilitiesAccess(false);

      if (user) {
        const facilityPerms = {};
        user.facility_and_permissions?.forEach((fp) => {
          facilityPerms[fp.facilityId + ''] = fp.permissions;
        });
        setFacilityPermissions(facilityPerms);

        const orgPerms = {};
        if (user.organization_permissions?.permissions) {
          const orgId = String(user.organization_permissions.organizationId);
          orgPerms[orgId] = user.organization_permissions.permissions;
        } else if (selectedOrganization) {
          orgPerms[selectedOrganization] = {};
        } else if (user.user_data?.organization_id) {
          const orgId = String(user.user_data.organization_id);
          orgPerms[orgId] = {};
        }
        setOrganizationPermissions(orgPerms);
      }
    }
  };

  const handleRoleChange = (roleId, hasRoleBasedFacilitiesAccess) => {
    setGuardError(null);
    setSelectedRoleId(roleId);
    setRoleBasedFacilitiesAccess(hasRoleBasedFacilitiesAccess);
  };

  const handleOrganizationChange = (organizationId) => {
    const oldOrgId = selectedOrganization;
    setSelectedOrganization(organizationId);

    if (organizationId !== oldOrgId) {
      const newOrgPermissions = {};
      if (organizationId) {
        newOrgPermissions[organizationId] = {};
      }
      setOrganizationPermissions(newOrgPermissions);
    }
  };

  const handleAccessModeChange = (newMode) => {
    setGuardError(null);
    setAccessMode(newMode);

    if (newMode !== FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES) {
      setSelectedFacilities([]);
    }

    if (newMode !== FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS) {
      setSelectedFacilityGroupIds([]);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const newSearchParams = new URLSearchParams(searchParams);
    if (newValue === 0) {
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', newValue.toString());
    }
    setSearchParams(newSearchParams);
  };

  const handleSave = async () => {
    try {
      let formData;
      let successMsg;

      if (activeTab === 0) {
        if (basicInfoRef.current && basicInfoRef.current.validateForm()) {
          if (permissionsType === 'role') {
            formData = {
              email: basicInfo.email,
              name: basicInfo.userName || basicInfo.name,
              mfa_type: basicInfo.mfa_type,
              language_preference: basicInfo.languagePreference,
              permissions_type: permissionsType,
              role_id: selectedRoleId,
            };
          } else {
            formData = {
              email: basicInfo.email,
              name: basicInfo.userName || basicInfo.name,
              mfa_type: basicInfo.mfa_type,
              language_preference: basicInfo.languagePreference,
              has_all_facilities_access:
                accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES,
              facility_ids:
                accessMode === FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES
                  ? selectedFacilities.map((facilityId) => parseInt(facilityId))
                  : [],
              organization_id: selectedOrganization
                ? parseInt(selectedOrganization)
                : null,
              permissions_type: permissionsType,
            };

            const facilityPermissionsArray = [];
            user.facility_and_permissions?.forEach((fp) => {
              if (fp.permissions && Object.keys(fp.permissions).length > 0) {
                facilityPermissionsArray.push({
                  facilityId: fp.facilityId,
                  permissions: fp.permissions,
                });
              }
            });

            if (facilityPermissionsArray.length > 0) {
              formData.facility_permissions = facilityPermissionsArray;
            }

            if (user.organization_permissions?.permissions) {
              formData.organization_permissions =
                user.organization_permissions.permissions;
            }
          }

          successMsg = 'Basic information updated successfully';
        } else {
          return; // INFO: Validation failed
        }
      } else if (activeTab === 1 || activeTab === 2) {
        formData = {
          email: user.user_data.email,
          name: user.user_data.name,
          mfa_type: user.user_data.mfa_type,
          language_preference: user.user_data.language_preference,
          facility_ids: selectedFacilities.map((facilityId) =>
            parseInt(facilityId),
          ),
          organization_id: selectedOrganization
            ? parseInt(selectedOrganization)
            : null,
          permissions_type: permissionsType,
        };

        if (permissionsType === 'role') {
          formData.role_id = selectedRoleId;
        } else {
          const facilityPermissionsArray = [];
          Object.entries(facilityPermissions).forEach(
            ([facilityId, permissions]) => {
              if (
                !isNaN(facilityId) &&
                permissions &&
                Object.keys(permissions).length > 0
              ) {
                facilityPermissionsArray.push({
                  facilityId: parseInt(facilityId),
                  permissions: permissions,
                });
              }
            },
          );

          if (facilityPermissionsArray.length > 0) {
            formData.facility_permissions = facilityPermissionsArray;
          }

          Object.entries(organizationPermissions).forEach(
            ([orgId, permissions]) => {
              if (
                !isNaN(orgId) &&
                permissions &&
                Object.keys(permissions).length > 0
              ) {
                formData.organization_permissions = permissions;
              }
            },
          );
        }

        successMsg = 'Permissions updated successfully';
      } else if (activeTab === 3) {
        formData = {
          first_name: employeeInfo.first_name,
          last_name: employeeInfo.last_name,
          employee_number: employeeInfo.employee_number,
          management_level: employeeInfo.management_level,
          approval_limit: employeeInfo.approval_limit,
          job_code: employeeInfo.job_code,
          job_title: employeeInfo.job_title,
          department: employeeInfo.department,
        };
        successMsg = 'Employee information updated successfully';
      }

      if (formData) {
        await updateUser(userId, formData);

        if (activeTab === 0 && permissionsType === 'role') {
          try {
            await UsersApi.updateFacilityUserScope(
              userId,
              buildFacilityUserScopePayload({
                accessMode,
                facilityIds: selectedFacilities,
                facilityGroupIds: selectedFacilityGroupIds,
              }),
            );
          } catch (scopeError) {
            console.error('Error saving facility user scope:', scopeError);
            setSuccessMessage(
              'Basic information updated, but saving facility access failed. Review the user scope and save again.',
            );
            setShowSuccessAlert(true);
            await refetch();
            return;
          }
        }

        await refetch();

        setSuccessMessage(successMsg);
        setShowSuccessAlert(true);

        setTimeout(() => {
          const successAlert = document.getElementById('success-alert');
          if (successAlert) {
            successAlert.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 100);
      }
    } catch (err) {
      console.error('Error updating user:', err);
    }
  };

  const handleBack = () => {
    navigate('/admin/usermanagement');
  };

  const handleSuccessClose = () => {
    setShowSuccessAlert(false);
    setSuccessMessage('');
  };

  const handleResendActivation = async () => {
    try {
      setResendLoading(true);
      setResendError(null);
      await axios.post(`${API_BASE_URL}/admin/user/resend_activation_email`, {
        email: user.user_data.email,
        username: user.user_data.email,
        name: user.user_data.name,
      });

      setSuccessMessage('Activation email sent successfully');
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 5000);
    } catch (err) {
      console.error('Error resending activation:', err);
      const errorMessage =
        err.response?.data?.message || 'Failed to send activation email';
      setResendError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
              <PersonIcon sx={{ color: 'white', mr: 1 }} />
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500 }}
                component='h4'>
                User Details
              </Typography>
            </Box>
          </CardHeader>
        }>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (fetchError) {
    return (
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
              <PersonIcon sx={{ color: 'white', mr: 1 }} />
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500 }}
                component='h4'>
                User Details
              </Typography>
            </Box>
          </CardHeader>
        }>
        <Alert severity='error' sx={{ m: 2 }}>
          {fetchError}
        </Alert>
      </PageLayout>
    );
  }

  if (!user) {
    return (
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
              <PersonIcon sx={{ color: 'white', mr: 1 }} />
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500 }}
                component='h4'>
                User Details
              </Typography>
            </Box>
          </CardHeader>
        }>
        <Alert severity='warning' sx={{ m: 2 }}>
          User not found
        </Alert>
      </PageLayout>
    );
  }

  const hasFacilityAccess =
    (accessMode === FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS &&
      selectedFacilityGroupIds.length > 0) ||
    accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES ||
    (accessMode === FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES &&
      selectedFacilities.length > 0);
  const isFormDisabled =
    updateLoading ||
    resendLoading ||
    (activeTab === 0 &&
      (!basicInfoRef.current?.isFormValid || !hasFacilityAccess));

  return (
    <PageLayout
      height='auto'
      padding={{ pt: 2, px: 2, pb: 4 }}
      header={
        <CardHeader color='primary'>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleBack}
              sx={{
                mr: 2,
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}>
              <ArrowBackIcon sx={{ color: 'white' }} />
            </IconButton>
            <PersonIcon sx={{ color: 'white', mr: 1 }} />
            <Typography
              variant='h6'
              sx={{ color: 'white', fontWeight: 500 }}
              component='h4'>
              User Details: {user.user_data?.name}
            </Typography>
          </Box>
        </CardHeader>
      }>
      {showSuccessAlert && successMessage && (
        <Alert
          id='success-alert'
          severity='success'
          sx={{ mb: 3 }}
          onClose={handleSuccessClose}>
          {successMessage}
        </Alert>
      )}

      {updateError && (
        <Alert id='error-alert' severity='error' sx={{ mb: 3 }}>
          {updateError}
        </Alert>
      )}

      {guardError && (
        <Alert severity='warning' sx={{ mb: 3 }}>
          {guardError}
        </Alert>
      )}

      {resendError && (
        <Alert id='resend-error-alert' severity='error' sx={{ mb: 3 }}>
          {resendError}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant='scrollable'
          scrollButtons='auto'>
          <Tab
            icon={<PersonIcon />}
            iconPosition='start'
            label='User Details'
          />
          <Tab
            icon={<BusinessIcon />}
            iconPosition='start'
            label='Organization Permissions'
          />
          <Tab
            icon={<ShieldIcon />}
            iconPosition='start'
            label='Facility Permissions'
          />
          <Tab
            icon={<BadgeIcon />}
            iconPosition='start'
            label='Employee Info'
          />
          <Tab icon={<HistoryIcon />} iconPosition='start' label='History' />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <UserBasicInfoSection
          ref={basicInfoRef}
          user={user}
          onFormChange={handleBasicInfoChange}
          facilities={facilities}
          organizations={organizations}
          selectedFacilities={selectedFacilities}
          onSelectedFacilitiesChange={setSelectedFacilities}
          onResendActivation={handleResendActivation}
          resendLoading={resendLoading}
          permissionsType={permissionsType}
          onPermissionsTypeChange={handlePermissionsTypeChange}
          selectedRoleId={selectedRoleId}
          onRoleChange={handleRoleChange}
          selectedOrganization={selectedOrganization}
          onOrganizationChange={handleOrganizationChange}
          onAccessModeChange={handleAccessModeChange}
          roleBasedFacilitiesAccess={roleBasedFacilitiesAccess}
          setRoleBasedFacilitiesAccess={setRoleBasedFacilitiesAccess}
          accessMode={accessMode}
          selectedFacilityGroupIds={selectedFacilityGroupIds}
          onSelectedFacilityGroupIdsChange={setSelectedFacilityGroupIds}
        />
      )}

      {activeTab === 1 && (
        <UserPermissionsSection
          permissions={organizationPermissions}
          onPermissionChange={(newPermissions, targetId) =>
            handlePermissionChange(newPermissions, targetId, 'organization')
          }
          permissionsTemplate={permissionsTemplate}
          facilities={facilities}
          organizations={organizations}
          selectedFacilities={selectedFacilities}
          editingUserId={user.user_data?.id}
          permissionsType={permissionsType}
          onPermissionsTypeChange={handlePermissionsTypeChange}
          selectedRoleId={selectedRoleId}
          permissionLevel='organization'
          currentUser={user.user_data}
        />
      )}

      {activeTab === 2 && (
        <UserPermissionsSection
          permissions={facilityPermissions}
          onPermissionChange={(newPermissions, targetId) =>
            handlePermissionChange(newPermissions, targetId, 'facility')
          }
          permissionsTemplate={permissionsTemplate}
          facilities={facilities}
          organizations={organizations}
          selectedFacilities={selectedFacilities}
          editingUserId={user.user_data?.id}
          permissionsType={permissionsType}
          onPermissionsTypeChange={handlePermissionsTypeChange}
          selectedRoleId={selectedRoleId}
          permissionLevel='facility'
          currentUser={user.user_data}
        />
      )}

      {activeTab === 3 && (
        <UserEmployeeInfoSection
          ref={employeeInfoRef}
          user={user}
          onFormChange={setEmployeeInfo}
        />
      )}

      {activeTab === 4 && <UserHistorySection userId={userId} />}

      {activeTab <= 3 &&
        !(activeTab > 0 && activeTab < 3 && permissionsType === 'role') && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 3 }}>
            <Button
              onClick={handleSave}
              variant='contained'
              startIcon={
                updateLoading ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                  <SaveIcon sx={{ color: 'white' }} />
                )
              }
              disabled={isFormDisabled}
              sx={{
                color: 'white',
                '& .MuiButton-startIcon': {
                  color: 'white',
                },
              }}>
              {updateLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        )}
    </PageLayout>
  );
};

export default UserDetails;
